import type {
  OwnerFondoCampana, ResultadoEnvioCampana, SaldoRestringidoCampana,
} from '../types/fondosCampana';

/** El POST pudo confirmarse: conservar la solicitud y sus argumentos al reintentar. */
export class ResultadoCampanaInciertoError extends Error {
  constructor() {
    super('No se pudo verificar el resultado. Consulta o reintenta la misma solicitud antes de registrar otra operación.');
    this.name = 'ResultadoCampanaInciertoError';
  }
}

export function esResultadoCampanaIncierto(error: unknown): error is ResultadoCampanaInciertoError {
  return error instanceof ResultadoCampanaInciertoError;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function validarIdentificadorOperacionCampana(valor: unknown): string {
  if (typeof valor !== 'string' || !UUID.test(valor)) throw new ResultadoCampanaInciertoError();
  return valor;
}

/** Evita enviar fracciones binarias como 0.30000000000000004 a NUMERIC. */
export function sumarImportesCampana(anterior: number, entrega: number): number {
  const importes = [anterior, entrega];
  if (importes.some(valor => !Number.isFinite(valor) || valor < 0
    || Math.round(valor * 100) / 100 !== valor || !Number.isSafeInteger(Math.round(valor * 100)))) {
    throw new Error('Los importes deben ser válidos y tener hasta dos decimales.');
  }
  const centavos = Math.round(anterior * 100) + Math.round(entrega * 100);
  if (!Number.isSafeInteger(centavos)) throw new Error('El importe supera la precisión permitida.');
  return centavos / 100;
}

export function importePendienteCampana(comprometido: number, aportado: number): number {
  sumarImportesCampana(comprometido, 0);
  sumarImportesCampana(aportado, 0);
  return (Math.round(comprometido * 100) - Math.round(aportado * 100)) / 100;
}

/** Una respuesta de red incompleta no demuestra que la transacción se haya revertido. */
export function lanzarErrorOperacionCampana(error: unknown): never {
  if (typeof error === 'object' && error !== null && 'code' in error
    && typeof error.code === 'string'
    && /^(?:[0-9A-Z]{5}|PGRST\d{3})$/.test(error.code) && !error.code.startsWith('08')) {
    throw error;
  }
  throw new ResultadoCampanaInciertoError();
}
const ESTADOS: readonly string[] = [
  'pendiente', 'observado', 'confirmado', 'cerrado_sin_transferencia',
];

function esRegistro(valor: unknown): valor is Record<string, unknown> {
  return typeof valor === 'object' && valor !== null && !Array.isArray(valor);
}

function esUuid(valor: unknown): valor is string {
  return typeof valor === 'string' && UUID.test(valor);
}

function esUuidONull(valor: unknown): boolean {
  return valor === null || esUuid(valor);
}

function esTextoONull(valor: unknown): boolean {
  return valor === null || typeof valor === 'string';
}

function esNumero(valor: unknown): valor is number {
  return typeof valor === 'number' && Number.isFinite(valor);
}

function esFecha(valor: unknown): boolean {
  if (typeof valor !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(valor)) return false;
  const fecha = new Date(`${valor}T00:00:00Z`);
  return Number.isFinite(fecha.getTime()) && fecha.toISOString().slice(0, 10) === valor;
}

function esTimestamp(valor: unknown): boolean {
  return typeof valor === 'string' && Number.isFinite(Date.parse(valor));
}

function esTimestampONull(valor: unknown): boolean {
  return valor === null || esTimestamp(valor);
}

function esParAsientos(egreso: unknown, ingreso: unknown): boolean {
  return (egreso === null && ingreso === null)
    || (esUuid(egreso) && esUuid(ingreso) && egreso !== ingreso);
}

function esGeografiaEnvio(envio: Record<string, unknown>): boolean {
  if (envio.lumisial_id !== null) {
    return esUuid(envio.lumisial_id) && esUuid(envio.campana_id)
      && envio.origen_diocesis_id === null && esUuid(envio.destino_diocesis_id)
      && envio.destino_provincia_eclesiastica_id === null;
  }
  return envio.campana_id === null && esUuid(envio.origen_diocesis_id)
    && envio.destino_diocesis_id === null && esUuid(envio.destino_provincia_eclesiastica_id);
}

function esCabeceraEnvio(envio: Record<string, unknown>): boolean {
  return esUuid(envio.id) && esUuid(envio.solicitud_id) && esUuid(envio.campana_master_id)
    && esUuidONull(envio.creado_por_usuario_id) && esUuidONull(envio.confirmado_por_usuario_id)
    && typeof envio.estado === 'string' && ESTADOS.includes(envio.estado)
    && esNumero(envio.revision) && Number.isInteger(envio.revision) && envio.revision > 0
    && esNumero(envio.monto) && envio.monto > 0 && esGeografiaEnvio(envio);
}

function esContenidoEnvio(envio: Record<string, unknown>): boolean {
  return esFecha(envio.fecha_envio) && esTimestamp(envio.created_at)
    && esTimestampONull(envio.fecha_confirmacion) && esTextoONull(envio.descripcion)
    && esTextoONull(envio.nota_admin) && typeof envio.notificacion_admin_enviada === 'boolean'
    && typeof envio.notificacion_jal_enviada === 'boolean';
}

export function validarResultadoEnvio(valor: unknown): ResultadoEnvioCampana {
  if (!esRegistro(valor) || !esRegistro(valor.envio) || !esCabeceraEnvio(valor.envio)
    || !esContenidoEnvio(valor.envio) || !esParAsientos(valor.egreso_id, valor.ingreso_id)) {
    throw new ResultadoCampanaInciertoError();
  }
  return valor as unknown as ResultadoEnvioCampana;
}

const CAMPOS_MONETARIOS = [
  'recaudado', 'recibido_confirmado', 'enviado_confirmado', 'otros_egresos',
  'saldo_inicial', 'saldo_registrado', 'enviado_pendiente', 'enviado_observado',
  'no_disponible', 'disponible',
] as const;
type CampoMonetario = typeof CAMPOS_MONETARIOS[number];
type FilaSaldo = Record<CampoMonetario, number> & { readonly campana_id: string; readonly nombre: string };

function esFilaSaldo(valor: unknown): valor is FilaSaldo {
  return esRegistro(valor) && esUuid(valor.campana_id) && typeof valor.nombre === 'string'
    && CAMPOS_MONETARIOS.every(k => esNumero(valor[k]));
}

function importesIguales(a: number, b: number): boolean {
  return Math.abs(a - b) < 0.005;
}

function validarContabilidadSaldo(fila: FilaSaldo): void {
  const inmovilizado = fila.enviado_pendiente + fila.enviado_observado;
  const saldo = fila.saldo_inicial + fila.recaudado + fila.recibido_confirmado
    - fila.enviado_confirmado - fila.otros_egresos;
  const negativos = CAMPOS_MONETARIOS.filter(k => k !== 'saldo_registrado' && k !== 'disponible');
  if (negativos.some(k => fila[k] < 0) || !importesIguales(fila.no_disponible, inmovilizado)
    || !importesIguales(fila.disponible, fila.saldo_registrado - inmovilizado)
    || !importesIguales(fila.saldo_registrado, saldo)) {
    throw new Error('El saldo del fondo no coincide con sus movimientos y reservas.');
  }
}

function mapearSaldo(fila: FilaSaldo, owner: OwnerFondoCampana): SaldoRestringidoCampana {
  validarContabilidadSaldo(fila);
  return {
    campanaId: fila.campana_id, nombre: fila.nombre, owner: { ...owner },
    recaudadoMonetario: fila.recaudado, recibidoConfirmado: fila.recibido_confirmado,
    enviadoConfirmado: fila.enviado_confirmado, otrosEgresos: fila.otros_egresos,
    saldoInicial: fila.saldo_inicial, saldoRegistrado: fila.saldo_registrado,
    enviadoPendiente: fila.enviado_pendiente, enviadoObservado: fila.enviado_observado,
    noDisponible: fila.no_disponible, disponible: fila.disponible,
  };
}

export function validarSaldosRestringidosCampana(
  valor: unknown, owner: OwnerFondoCampana,
): SaldoRestringidoCampana[] {
  if (!['lumisial', 'diocesis', 'provincia'].includes(owner.tipo) || !esUuid(owner.id)) {
    throw new Error('El fondo debe pertenecer a un Lumisial, diócesis o Junta Administrativa Nacional.');
  }
  if (!Array.isArray(valor) || !valor.every(esFilaSaldo)) {
    throw new Error('No se recibió un saldo completo de campañas.');
  }
  if (new Set(valor.map(fila => fila.campana_id)).size !== valor.length) {
    throw new Error('El saldo contiene una campaña repetida.');
  }
  return valor.map(fila => mapearSaldo(fila, owner));
}
